import React, { useEffect, useRef } from "react";

interface CollapsingSectionProps extends React.HTMLAttributes<HTMLDivElement> {
    isExiting: boolean;
    onExitComplete: () => void;
    /** Exit duration in ms — keep in sync with CSS transition timing. */
    durationMs?: number;
    children: React.ReactNode;
}

/**
 * When `isExiting` becomes true, measures current height then animates to 0
 * so following siblings slide up to fill the gap.
 */
const CollapsingSection: React.FC<CollapsingSectionProps> = ({
    isExiting,
    onExitComplete,
    durationMs = 350,
    children,
    className = "",
    style,
    ...rest
}) => {
    const ref = useRef<HTMLDivElement>(null);
    const exitStartedRef = useRef(false);
    const completedRef = useRef(false);
    const onExitCompleteRef = useRef(onExitComplete);
    onExitCompleteRef.current = onExitComplete;

    useEffect(() => {
        const el = ref.current;
        if (!isExiting || !el || exitStartedRef.current) return;

        exitStartedRef.current = true;
        completedRef.current = false;
        const measuredHeight = el.getBoundingClientRect().height;

        el.style.overflow = "hidden";
        el.style.height = `${measuredHeight}px`;
        el.style.pointerEvents = "none";
        // Force layout so the browser registers the starting height before collapsing.
        void el.offsetHeight;

        el.classList.add("collapsing-section--exiting");
        el.style.height = "0px";

        const finish = () => {
            if (completedRef.current) return;
            completedRef.current = true;
            onExitCompleteRef.current();
        };

        const handleTransitionEnd = (event: TransitionEvent) => {
            if (event.target !== el || event.propertyName !== "height") return;
            finish();
        };

        el.addEventListener("transitionend", handleTransitionEnd);
        const fallbackTimer = window.setTimeout(finish, durationMs + 50);

        return () => {
            el.removeEventListener("transitionend", handleTransitionEnd);
            window.clearTimeout(fallbackTimer);
        };
    }, [isExiting, durationMs]);

    useEffect(() => {
        if (!isExiting) {
            exitStartedRef.current = false;
            completedRef.current = false;
            const el = ref.current;
            if (el) {
                el.classList.remove("collapsing-section--exiting");
                el.style.height = "";
                el.style.overflow = "";
                el.style.pointerEvents = "";
            }
        }
    }, [isExiting]);

    return (
        <div ref={ref} className={`collapsing-section ${className}`.trim()} style={style} {...rest}>
            {children}
        </div>
    );
};

export default CollapsingSection;
